import { decodePassengers } from 'utils/Reserbus';
import { getQuickPurchasePassengerCount } from 'utils/quickPurchasePassengers';
import { MAX_QUICK_PURCHASE_PASSENGERS } from 'constants/quickPurchase';
import { isQuickPurchaseBlockedByOverbooking } from 'utils/quickPurchaseAvailability';

/**
 * Evaluate base eligibility conditions for Quick Purchase.
 * Does not depend on per-trip data.
 * @param {object} params - Eligibility inputs.
 * @param {boolean} params.quickPurchaseEnabled - Feature flag value.
 * @param {object} params.searchPassengers - Passengers object from useSearch.
 * @param {Array} params.frequentPassengers - Frequent passengers from useUserPreferences.
 * @param {object} params.features - Whitelabel features object.
 * @param {boolean} params.isRoundTrip - Whether the search is a round trip.
 * @param {boolean} params.isExchange - Whether the flow is an exchange.
 * @param {boolean} [params.isBulkTicket] - Whether the route is bulk ticket.
 * @param {boolean} [params.isQuickPurchaseLoyaltySettled] - Whether the loyalty status has been resolved.
 * @param {boolean} [params.userIsLoggedInWithAnyLoyaltyProgram] - Whether the user is logged in with a loyalty program.
 * @param {boolean} [params.skipLoyaltyGates] - When true, ignores the loyalty-related gates (used by telemetry).
 * @returns {{ eligible: boolean, eligibleWithoutPassengers: boolean, searchPassengerCount: number, mergedPassengerCount: number, hasEnoughPassengers: boolean, isOnlyAdults: boolean }}
 */
export function getBaseEligibility({
  quickPurchaseEnabled,
  searchPassengers,
  frequentPassengers,
  features,
  isRoundTrip,
  isExchange,
  isBulkTicket = false,
  isQuickPurchaseLoyaltySettled = true,
  userIsLoggedInWithAnyLoyaltyProgram = false,
  skipLoyaltyGates = false,
}) {
  const decoded = decodePassengers(searchPassengers?.passengers || 'A1');
  const searchPassengerCount = decoded.adult || 0;
  const isOnlyAdults =
    searchPassengerCount > 0 && !decoded.child && !decoded.minor && !decoded.infant;
  const mergedPassengerCount = getQuickPurchasePassengerCount(frequentPassengers, features);
  const isWithinPassengerLimit = searchPassengerCount <= MAX_QUICK_PURCHASE_PASSENGERS;
  const hasEnoughPassengers = mergedPassengerCount >= searchPassengerCount;

  const passesLoyalty =
    skipLoyaltyGates ||
    (Boolean(isQuickPurchaseLoyaltySettled) && !userIsLoggedInWithAnyLoyaltyProgram);

  const eligibleWithoutPassengers =
    Boolean(quickPurchaseEnabled) &&
    isOnlyAdults &&
    !isRoundTrip &&
    !isExchange &&
    !isBulkTicket &&
    passesLoyalty;

  const eligible = eligibleWithoutPassengers && isWithinPassengerLimit && hasEnoughPassengers;

  return {
    eligible,
    eligibleWithoutPassengers,
    searchPassengerCount,
    mergedPassengerCount,
    hasEnoughPassengers,
    isOnlyAdults,
  };
}

/**
 * Whether the trip hands the user off to another brand instead of being purchased
 * locally. Mirrors the redirect rule used by trip selection (`TripItem.handleOnSelect`):
 * the backend marks the trip with `redirect_to` (read as `redirectTo`), but the funnel
 * only redirects when the `TRIP_REDIRECTION_LEAD` feature is enabled. When the feature
 * is off the trip is bought locally, so it is not treated as a redirect trip.
 * @param {object} trip - Trip object from search results.
 * @param {object} features - Whitelabel features object.
 * @returns {boolean}
 */
export function isRedirectTrip(trip, features) {
  return Boolean(features?.TRIP_REDIRECTION_LEAD && trip?.redirectTo);
}

/**
 * Evaluate per-trip eligibility for Quick Purchase.
 * @param {object} trip - Trip object from search results.
 * @param {boolean} baseEligible - Result from getBaseEligibility.
 * @param {object} [features] - Whitelabel features object (for the redirect gate).
 * @returns {boolean}
 */
export function getTripEligibility(trip, baseEligible, features) {
  return (
    Boolean(baseEligible) &&
    Boolean(trip?.allowsSeatSelection) &&
    !trip?.hasConnections &&
    !isQuickPurchaseBlockedByOverbooking(trip) &&
    !isRedirectTrip(trip, features)
  );
}

/**
 * Evaluate results-level eligibility: at least one trip is eligible for Quick Purchase.
 * @param {Array} trips - List of trip objects from search results.
 * @param {boolean} baseEligible - Result from getBaseEligibility.
 * @param {object} [features] - Whitelabel features object (for the redirect gate).
 * @returns {boolean}
 */
export function getResultsEligibility(trips, baseEligible, features) {
  if (!baseEligible) return false;
  return Array.isArray(trips) && trips.some((t) => getTripEligibility(t, true, features));
}

/**
 * Evaluate per-trip eligibility for Quick Purchase from the seat selector.
 * Does not require allowsSeatSelection (the user is already in seats) and does
 * not require passenger-count gates (the seat count drives the passenger param).
 * @param {object} trip - Trip object from search results.
 * @param {boolean} eligibleWithoutPassengers - Base eligibility ignoring passenger-count gates.
 * @param {object} [features] - Whitelabel features object (for the redirect gate).
 * @returns {boolean}
 */
export function getInSeatsEligibility(trip, eligibleWithoutPassengers, features) {
  return (
    Boolean(eligibleWithoutPassengers) &&
    !trip?.hasConnections &&
    !isQuickPurchaseBlockedByOverbooking(trip) &&
    !isRedirectTrip(trip, features)
  );
}
